import { useEffect, useState } from 'react';
import { useUIStore, type ScreenName } from '@/stores/uiStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { useAuthStore } from '@/auth/authStore';
import { useAuthBootstrap } from '@/auth/useAuthBootstrap';
import { LoginScreen } from '@/screens/LoginScreen';
import { HomeScreen } from '@/screens/HomeScreen';
import { MatchmakingScreen } from '@/screens/MatchmakingScreen';
import { MatchScreen } from '@/screens/MatchScreen';
import { ResultScreen } from '@/screens/ResultScreen';
import { LeaderboardScreen } from '@/screens/LeaderboardScreen';
import { SettingsScreen } from '@/screens/SettingsScreen';
import { ShopScreen } from '@/screens/ShopScreen';
import { InventoryScreen } from '@/screens/InventoryScreen';
import { FriendsScreen } from '@/screens/FriendsScreen';
import { ClanScreen } from '@/screens/ClanScreen';
import { RoomScreen } from '@/screens/RoomScreen';
import { BossSelectScreen } from '@/screens/BossSelectScreen';
import { BossBattleScreen } from '@/screens/BossBattleScreen';
import { ChatDock, ChatNotifier } from '@/components/chat/ChatDock';
import { ToastHost } from '@/components/ToastHost';
import { ConfirmHost } from '@/components/ConfirmHost';
import { PlayGateModal } from '@/components/PlayGateModal';
import { UpdateGateModal } from '@/components/UpdateGateModal';
import {
  AnnouncementModal,
  MaintenanceScreen,
} from '@/components/AppRuntimeGate';
import { runUpdateCheck } from '@/version/updateCheck';
import { RoomNotifier } from '@/components/RoomNotifier';
import { ClanNotifier } from '@/components/ClanNotifier';
import { audio } from '@/audio/AudioService';
import { native } from '@/platform/native';
import { disconnectSocket, getSocket } from '@/net/socket';
import {
  prefetchAssets,
  type AssetPrefetchProgress,
} from '@/net/assetPrefetch';
import {
  fetchRuntimeConfig,
  type RuntimeConfig,
} from '@/runtime/runtimeConfig';
import { EV } from '@shared/protocol';

declare global {
  interface Window {
    __cdNav?: (screen: ScreenName, payload?: unknown) => void;
  }
}

// Nudge the realtime socket back to life after the app returns to the
// foreground. If it's already connected this is a cheap no-op; if it dropped
// while backgrounded, connect() short-circuits the reconnect backoff so the
// room re-sync (RoomNotifier 'connect' handler) runs immediately.
function wake() {
  const s = getSocket();
  if (!s.connected) s.connect();
}

const SCREEN_MAP: Record<ScreenName, () => JSX.Element> = {
  login: () => <LoginScreen />,
  home: () => <HomeScreen />,
  matching: () => <MatchmakingScreen />,
  match: () => <MatchScreen />,
  result: () => <ResultScreen />,
  leaderboard: () => <LeaderboardScreen />,
  settings: () => <SettingsScreen />,
  shop: () => <ShopScreen />,
  inventory: () => <InventoryScreen />,
  friends: () => <FriendsScreen />,
  clan: () => <ClanScreen />,
  room: () => <RoomScreen />,
  bossSelect: () => <BossSelectScreen />,
  bossBattle: () => <BossBattleScreen />,
};

export default function App() {
  const screen = useUIStore((s) => s.screen);
  const go = useUIStore((s) => s.go);
  const showScanlines = useUIStore((s) => s.showScanlines);
  const colorBlind = useSettingsStore((s) => s.colorBlind);
  const token = useAuthStore((s) => s.token);
  const authStatus = useAuthStore((s) => s.status);
  const hasToken = token != null;
  const [assetsReady, setAssetsReady] = useState(
    () => useAuthStore.getState().token == null,
  );
  const [assetProgress, setAssetProgress] = useState<AssetPrefetchProgress>({
    phase: 'manifest',
    loaded: 0,
    total: 0,
  });
  const [runtimeConfig, setRuntimeConfig] = useState<RuntimeConfig | null>(null);
  const [runtimeChecked, setRuntimeChecked] = useState(false);
  const [announcementDismissed, setAnnouncementDismissed] = useState(false);

  // Hydrate auth from stored JWT (fetch /me) and gate screens.
  useAuthBootstrap();

  // Runtime controls are checked before opening the app. The server evaluates
  // the optional JWT and only clears maintenance mode for admin-configured
  // test accounts. Network failures fail open so an API hiccup cannot
  // permanently strand a previously authenticated build.
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    let inFlight = false;
    let completedOnce = false;
    setRuntimeChecked(false);

    const refresh = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const next = await fetchRuntimeConfig(token, controller.signal);
        if (!active) return;
        setRuntimeConfig(next);
      } catch (error: unknown) {
        if (!active) return;
        if (error instanceof DOMException && error.name === 'AbortError') return;
        // Keep the last known control state after a transient polling failure.
        // Only the first failed launch check falls back to an unrestricted app.
        if (!completedOnce) setRuntimeConfig(null);
      } finally {
        inFlight = false;
        if (active) {
          completedOnce = true;
          setRuntimeChecked(true);
        }
      }
    };

    void refresh();
    const intervalId = window.setInterval(() => void refresh(), 30_000);
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      active = false;
      controller.abort();
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [token]);

  // An admin may enable maintenance while players are already connected.
  // Receive the server push before it closes the socket so the gate appears
  // immediately instead of waiting for the 30-second config poll.
  useEffect(() => {
    if (!token) return;
    const socket = getSocket({ token });
    const onMaintenance = ({ message }: { message: string }) => {
      setAnnouncementDismissed(true);
      setRuntimeConfig((current) => ({
        minVersion: current?.minVersion ?? '0.0.0',
        latestVersion: current?.latestVersion ?? '0.0.0',
        announcement: current?.announcement ?? null,
        maintenance: { active: true, message },
      }));
      setRuntimeChecked(true);
      disconnectSocket();
    };
    socket.on(EV.APP_MAINTENANCE, onMaintenance);
    return () => {
      socket.off(EV.APP_MAINTENANCE, onMaintenance);
    };
  }, [token]);

  // Check the app version against the server on launch (native only). Shows a
  // soft nudge / hard gate if the build is behind. Fire-and-forget; silent on
  // failure so a flaky network never locks anyone out.
  useEffect(() => { void runUpdateCheck(); }, []);

  // Audio bootstrap: load manifest once + arm the unlock listener so the
  // first user gesture enables sound (browser autoplay gate). After manifest
  // loads, trigger BGM for the current screen so that the first play call
  // actually has URLs to use (startBgm is a no-op when manifest is empty).
  useEffect(() => {
    void audio.loadManifest().then(() => {
      audio.applyScreenBgm(useUIStore.getState().screen);
    });
    audio.installUnlockHandler();
    // Native lifecycle (Capacitor): pause/resume BGM when app backgrounds.
    let offPause = () => {};
    let offResume = () => {};
    void native.init().then(() => {
      offPause = native.onPause(() => audio.pauseAllBgm());
      offResume = native.onResume(() => { audio.resumeAllBgm(); wake(); });
    });
    return () => { offPause(); offResume(); };
  }, []);

  // Reconnect-on-wake + audio mute/unmute on visibility change.
  // Covers web/PWA — native uses Capacitor onPause/onResume above.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        audio.resumeAllBgm();
        wake();
      } else {
        audio.pauseAllBgm();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', wake);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', wake);
    };
  }, []);

  // Mirror SFX/Music slider changes into live BGM elements.
  const sfxVolume = useSettingsStore((s) => s.sfx);
  const musicVolume = useSettingsStore((s) => s.music);
  useEffect(() => { audio.refreshVolumes(); }, [sfxVolume, musicVolume]);

  // App BGM — single track per screen; logic lives in applyScreenBgm so the
  // post-manifest callback can reuse it without duplicating the mapping.
  useEffect(() => { audio.applyScreenBgm(screen); }, [screen]);

  useEffect(() => {
    window.__cdNav = (s, payload) => go(s, payload);
    return () => {
      delete window.__cdNav;
    };
  }, [go]);

  // Color-blind palette swap at the CSS-variable level
  useEffect(() => {
    const root = document.documentElement.style;
    if (colorBlind) {
      root.setProperty('--c-red', '#D81B60');
      root.setProperty('--c-blue', '#1E88E5');
      root.setProperty('--c-yellow', '#FFC107');
      root.setProperty('--c-purple', '#A0522D');
    } else {
      root.setProperty('--c-red', '#E63946');
      root.setProperty('--c-blue', '#3B82F6');
      root.setProperty('--c-yellow', '#F4C724');
      root.setProperty('--c-purple', '#9B5DE5');
    }
  }, [colorBlind]);

  const Screen = SCREEN_MAP[screen];
  const maintenanceActive = runtimeConfig?.maintenance.active === true;
  // While we're verifying a persisted token, suppress the login flash. Once
  // status flips to 'authed'/'anon', useAuthBootstrap has navigated to the
  // correct screen.
  const showSplash =
    !runtimeChecked
    || (
      hasToken
      && (
        authStatus === 'unknown'
        || authStatus === 'loading'
        || !assetsReady
      )
    );

  // Authenticated screens do not open until remote images/videos and Pixi
  // textures have been warmed. Individual asset failures are swallowed by the
  // prefetcher, so this gate cannot be held by one missing admin upload.
  useEffect(() => {
    if (!hasToken || maintenanceActive) {
      setAssetsReady(true);
      return;
    }
    if (!runtimeChecked) return;
    let active = true;
    setAssetsReady(false);
    void prefetchAssets((next) => {
      if (active) setAssetProgress(next);
    }).finally(() => {
      if (active) setAssetsReady(true);
    });
    return () => {
      active = false;
    };
  }, [hasToken, maintenanceActive, runtimeChecked]);

  return (
    <div className="stage">
      <div className="game-viewport">
        {showScanlines && (
          <div
            className="scanlines"
            style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 80 }}
          />
        )}
        {maintenanceActive ? (
          <MaintenanceScreen
            message={runtimeConfig?.maintenance.message ?? ''}
          />
        ) : showSplash ? (
          <BootSplash progress={assetProgress} />
        ) : (
          <div key={screen} style={{ position: 'absolute', inset: 0 }}>
            <Screen />
          </div>
        )}
        {/* Global floating chat — visible on lobby/home/room/result/leaderboard.
            Hidden during login + active gameplay so it never blocks UI. The
            notifier mounts always-on to maintain unread counters. */}
        {!showSplash && !maintenanceActive && (
          <>
            <ChatNotifier />
            <RoomNotifier />
            <ClanNotifier />
            <ChatDock
              hidden={
                screen === 'login'
                || screen === 'match'
                || screen === 'matching'
                || screen === 'bossBattle'
              }
            />
            <ToastHost />
            <ConfirmHost />
            <PlayGateModal />
          </>
        )}
        {!showSplash
          && !maintenanceActive
          && runtimeConfig?.announcement
          && !announcementDismissed && (
            <AnnouncementModal
              announcement={runtimeConfig.announcement}
              onClose={() => setAnnouncementDismissed(true)}
            />
          )}
        {/* Version gate sits outside the splash guard so a hard gate can block
            the app even before the splash hides. */}
        <UpdateGateModal />
      </div>
    </div>
  );
}

function BootSplash({ progress }: { progress: AssetPrefetchProgress }) {
  const percent =
    progress.total > 0
      ? Math.min(100, Math.round((progress.loaded / progress.total) * 100))
      : 0;
  const label =
    progress.phase === 'manifest'
      ? 'PREPARING ASSETS'
      : progress.phase === 'textures'
        ? 'PREPARING GAME'
        : 'LOADING ASSETS';
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'radial-gradient(ellipse at center, var(--bg-mid) 0%, var(--bg-deep) 70%)',
        color: 'var(--c-cyan)',
        fontFamily: 'var(--f-display)',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <div
        style={{
          fontFamily: 'var(--f-display)',
          fontSize: 14,
          letterSpacing: '0.18em',
        }}
      >
        {label}
      </div>
      <div
        style={{
          width: 'min(280px, 70vw)',
          height: 6,
          overflow: 'hidden',
          borderRadius: 99,
          border: '1px solid rgba(34,211,238,0.35)',
          background: 'rgba(255,255,255,0.06)',
        }}
      >
        <div
          style={{
            width: `${progress.phase === 'manifest' ? 8 : percent}%`,
            height: '100%',
            borderRadius: 99,
            background: 'linear-gradient(90deg, var(--c-cyan), var(--c-purple))',
            boxShadow: '0 0 12px rgba(34,211,238,0.75)',
            transition: 'width 0.2s ease',
          }}
        />
      </div>
      <div
        className="mono"
        style={{ color: 'var(--ink-muted)', fontSize: 11, letterSpacing: '0.08em' }}
      >
        {progress.total > 0 ? `${progress.loaded}/${progress.total} · ${percent}%` : '…'}
      </div>
    </div>
  );
}
